const FormGroupMixin = () => ({
  props: {
    schema: {
      type: Object,
      required: true
    },
    mainFieldsOptions: {
      type: Object,
      default: () => ({})
    },

    fieldsOptions: {
      type: Object,
      default: () => ({})
    },

    labelLeft: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: false
    },

    noActions: {
      type: Boolean,
      default: false
    },
  },

  data: () => ({
    groupsValidationMessage: {},
    isDeletingGroupItem: null,
  }),

  computed: {
    mainSection () {
      return (this.schema || {}).main || null
    },

    mainForm () {
      if (!this.mainSection) return {}

      const mainFields = Object.keys(this.mainSection.fields || {})
      return mainFields.reduce((acc, fieldName) => {
        if (this.formData[fieldName] !== undefined) {
          acc[fieldName] = this.formData[fieldName]
        }
        return acc
      }, {})
    },

    groups () {
      const groups = (this.schema || {}).groups || []

      const finalGroup = groups.map(group => ({
        ...group,
        valuePath: `formData[${group.groupName}]`,
        forms: this.getGroupForms(group),
        slots: Object
          .entries((group || {}).fields)
          .filter(([ , field ]) => field.type === 'slot')
          .map(([ fieldName ]) => fieldName + '-slot')
      }))

      return finalGroup
    },
  },
  methods: {
    getGroupForms (group) {
      this.formData[group.groupName] = this.formData[group.groupName] || []
      if (this.formData[group.groupName].length) return this.formData[group.groupName]

      if (group.startWithForm) {
        this.formData[group.groupName] = [ {} ]
      }

      return this.formData[group.groupName]
    },

    getFieldOptions (group, form) {
      const { uniqueFieldOptions, groupName, fields = {} } = group
      const { fieldsOptions } = this || {}

      if (!uniqueFieldOptions) return fieldsOptions

      const uniqueFields = Object.entries(fields).reduce((acc, entry) => {
        if (entry[1]?.uniqueFieldOptions)
          acc.push({
            field: entry[0], optionsSrc: entry[1]?.optionsSrc, trackBy: entry[1]?.trackBy
          })

        return acc
      }, []);

      const groupForms = this.formData[groupName] || []

      const options = Object.entries(fieldsOptions).reduce((acc, option) => {
        const relatedFields = uniqueFields.filter(({ optionsSrc }) => option[0] === optionsSrc)

        if (!relatedFields.length) {
          const [ _, optionData ] = option

          acc[option[0]] = optionData
          return acc
        }

        // collect all used values from all fields that use this options source
        const allUsedValues = new Set()

        groupForms.forEach(formData => {
          relatedFields.forEach(({ field }) => {
            const value = formData[field]
            if (value != null) {
              allUsedValues.add(value)
            }
          })
        })

        // current form values must be allowed
        const currentFormValues = new Set()
        relatedFields.forEach(({ field }) => {
          const value = form[field]
          if (value != null) {
            currentFormValues.add(value)
          }
        })

        acc[option[0]] = option[1].filter(({ [relatedFields[0].trackBy]: trackBy }) => {
          return currentFormValues.has(trackBy) || !allUsedValues.has(trackBy)
        })

        return acc
      }, {})

      return {
        ...options,
      }
    },

    shouldDisableAddButton (group) {
      // if there is no uniqueFieldOptions flag active, do not disable
      if (!group.uniqueFieldOptions) return false

      const { fields = {}, groupName } = group

      // search for fields that have shouldBlockAddButtonIfEmpty
      const fieldsToCheck = Object.entries(fields).filter(([ , fieldConfig ]) =>
        fieldConfig.shouldBlockAddButtonIfEmpty && fieldConfig.uniqueFieldOptions
      )

      // if there are no fields to check, do not disable
      if (!fieldsToCheck.length) return false

      // get existing forms in the group
      const currentGroupData = this.formData[groupName] || []
      const totalFormsCount = currentGroupData.length

      // for each field that should block, check if there are available options
      return fieldsToCheck.some(([ , fieldConfig ]) => {
        const { optionsSrc } = fieldConfig
        if (!optionsSrc || !this.fieldsOptions || !this.fieldsOptions[optionsSrc]) {
          return false
        }

        // get the total number of unique options available
        const totalUniqueOptions = this.fieldsOptions[optionsSrc]?.length || 0

        // if we already have as many forms as unique options, disable
        if (totalFormsCount >= totalUniqueOptions) {
          return true
        }

        // simulate an empty form to check available options
        const emptyForm = {}
        const availableOptions = this.getFieldOptions(group, emptyForm)

        return !availableOptions[optionsSrc] || !availableOptions[optionsSrc].length
      })
    },

    async onValidate () {
      const { mainForm, groupForms } = this.$refs
      const allForms = [
        ...(this.mainSection && mainForm ? [ mainForm ] : []),
        ...groupForms || []
      ]

      const isValid = (await Promise.all(allForms
        .map(form => form.onValidate()))).every(Boolean)

      return isValid
    },

    handleDeleteGroupItem (group, index) {
      if (!this.$slots[`${group.groupName}-delete-modal`]) {
        this.deleteGroupItem(group, index)
        return
      }

      this.isDeletingGroupItem = [ group, index ]

      const { [group.groupName]: _, ...rest } = this.groupsValidationMessage
      this.groupsValidationMessage = { ...rest }
    },

    showDeleteModal (groupName, index) {
      return (this.isDeletingGroupItem || [])[0]?.groupName === groupName
        && (this.isDeletingGroupItem || [])[1] === index
    },

    deleteGroupItem (group = {}, index) {
      const currentForms = this.formData[group.groupName] || []
      if (!currentForms[index]) return

      const newForms = [ ...currentForms ]
      newForms.splice(index, 1)

      const newFormData = {
        ...this.formData,
        [group.groupName]: newForms
      }

      this.emitInput(newFormData)

      this.isDeletingGroupItem = null
    },

    addFormToGroup (group) {
      const { maxItems, groupName } = group;
      const currentGroupData = this.formData[groupName] || [];

      if (!maxItems || currentGroupData.length < maxItems) {
        const newFormData = {
          ...this.formData,
          [groupName]: [ ...currentGroupData, {} ]
        }

        this.emitInput(newFormData)

        const { [groupName]: _, ...rest } = this.groupsValidationMessage
        this.groupsValidationMessage = { ...rest }
      } else {
        this.groupsValidationMessage = {
          ...this.groupsValidationMessage,
          [groupName]: true
        }
      }
    },

    setMainForm (data) {
      const newFormData = { ...this.formData }

      if (this.mainSection) {
        const mainFields = Object.keys(this.mainSection.fields || {})
        mainFields.forEach(fieldName => {
          if (data[fieldName] !== undefined) {
            newFormData[fieldName] = data[fieldName]
          }
        })
      }

      this.emitInput(newFormData)
    },

    isItemDeletable (group) {
      const minItems = Math.max(group.requiredItems || 0, group.startWithForm ? 1 : 0)

      return group.forms?.length > minItems
    },

    setGroupForm (group, index, data) {
      const currentGroupData = this.formData[group.groupName] || []
      const newGroupData = [ ...currentGroupData ]
      newGroupData[index] = data

      const newFormData = {
        ...this.formData,
        [group.groupName]: newGroupData
      }

      this.emitInput(newFormData)
    }
  }
})

export default FormGroupMixin
